import { generateClient } from 'aws-amplify/data';
import logger from '../utils/logger.js';
import { getCurrentUser } from 'aws-amplify/auth';
import * as XLSX from 'xlsx';
import Papa from 'papaparse';
import {
  createNetworkError,
  createValidationError,
  createDataUnavailableError
} from '../utils/StandardErrorFormat.js';

const VALID_REMINDER_TYPES = ['follow_up', 'check_in', 'review_meeting', 'custom'];
const REMINDER_STORAGE_KEY = 'client_reminders';

/**
 * In-memory + sessionStorage store for reminders.
 * TODO: Migrate to GraphQL ClientReminder model when schema is available.
 * Structure: { id, clientId, campaignId?, reminderDate, reminderType, notes, status, createdAt }
 */
class ReminderStore {
  constructor() {
    this.reminders = this._loadFromStorage();
  }

  _loadFromStorage() {
    try {
      if (typeof sessionStorage === 'undefined') return [];
      const stored = sessionStorage.getItem(REMINDER_STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  }

  _saveToStorage() {
    try {
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.setItem(REMINDER_STORAGE_KEY, JSON.stringify(this.reminders));
      }
    } catch {
      // sessionStorage unavailable (e.g. private mode full)
    }
  }

  add(reminder) {
    this.reminders.push(reminder);
    this._saveToStorage();
    return reminder;
  }

  findById(id) {
    return this.reminders.find(r => r.id === id) || null;
  }

  update(id, updates) {
    const idx = this.reminders.findIndex(r => r.id === id);
    if (idx === -1) return null;
    this.reminders[idx] = { ...this.reminders[idx], ...updates };
    this._saveToStorage();
    return this.reminders[idx];
  }

  remove(id) {
    const idx = this.reminders.findIndex(r => r.id === id);
    if (idx === -1) return false;
    this.reminders.splice(idx, 1);
    this._saveToStorage();
    return true;
  }

  all() {
    return [...this.reminders];
  }
}

// Import normalization tables (merged from ClientImportService - Sprint 3 #51)
const VALID_RISK_PROFILES = ['Conservative', 'Moderate', 'Aggressive', 'Very Aggressive'];
const VALID_PORTFOLIO_SIZES = ['$0-50K', '$50K-250K', '$250K-1M', '$1M-5M', '$5M+'];

const RISK_PROFILE_MAP = {
  'conservative': 'Conservative',
  'cons': 'Conservative',
  'moderate': 'Moderate',
  'mod': 'Moderate',
  'medium': 'Moderate',
  'aggressive': 'Aggressive',
  'agg': 'Aggressive',
  'very aggressive': 'Very Aggressive',
  'very agg': 'Very Aggressive',
  'highest': 'Very Aggressive'
};

const PORTFOLIO_SIZE_MAP = {
  '$0-50k': '$0-50K',
  '0-50k': '$0-50K',
  'under 50k': '$0-50K',
  '$50k-250k': '$50K-250K',
  '50k-250k': '$50K-250K',
  '$250k-1m': '$250K-1M',
  '250k-1m': '$250K-1M',
  '$1m-5m': '$1M-5M',
  '1m-5m': '$1M-5M',
  '$5m+': '$5M+',
  '5m+': '$5M+',
  'over 5m': '$5M+'
};

class ClientService {
  constructor() {
    // Create service-specific logger
    this.logger = logger.child('ClientService');

    this._client = null;

    // In-memory reminder store (TODO: migrate to GraphQL)
    this._reminderStore = new ReminderStore();

    // Import enums (merged from ClientImportService - Sprint 3 #51)
    this.VALID_RISK_PROFILES = VALID_RISK_PROFILES;
    this.VALID_PORTFOLIO_SIZES = VALID_PORTFOLIO_SIZES;
    this.RISK_PROFILE_MAP = RISK_PROFILE_MAP;
    this.PORTFOLIO_SIZE_MAP = PORTFOLIO_SIZE_MAP;
  }

  /**
   * Lazy-load Amplify client (allows mocking in tests)
   */
  get client() {
    if (!this._client) {
      this._client = generateClient();
    }
    return this._client;
  }

  /**
   * Get current user ID with fallback for development
   */
  async getCurrentUserId() {
    try {
      const user = await getCurrentUser();
      return user.userId;
    } catch (error) {
      this.logger.warn('getCurrentUser failed, using fallback ID for development:', error);
      // Fallback for development/testing
      return 'dev-user-123';
    }
  }

  /**
   * Sanitize client data for Amplify/GraphQL
   */
  sanitizeClientData(clientData) {
    const sanitized = { ...clientData };
    
    // Handle date fields properly for AWS Amplify
    if (sanitized.dateOfBirth === '' || sanitized.dateOfBirth === undefined || sanitized.dateOfBirth === null) {
      // Remove the field entirely if empty - AWS Amplify handles optional fields better this way
      delete sanitized.dateOfBirth;
    } else if (sanitized.dateOfBirth) {
      // Ensure date is in proper format (YYYY-MM-DD for AWS Date type)
      // HTML date inputs already provide YYYY-MM-DD format, so we just need to validate it
      const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
      if (!dateRegex.test(sanitized.dateOfBirth)) {
        this.logger.warn('Invalid date format for dateOfBirth:', sanitized.dateOfBirth);
        delete sanitized.dateOfBirth;
      }
    }
    
    // Convert empty strings to null for optional string fields
    const optionalStringFields = ['phone', 'occupation', 'annualIncome', 'netWorth', 'notes', 'preferredTone', 'communicationPreference'];
    optionalStringFields.forEach(field => {
      if (sanitized[field] === '' || sanitized[field] === undefined) {
        delete sanitized[field]; // Remove empty fields entirely
      }
    });
    
    // Ensure investmentGoals is an array and filter out empty values
    if (sanitized.investmentGoals && Array.isArray(sanitized.investmentGoals)) {
      sanitized.investmentGoals = sanitized.investmentGoals.filter(goal => goal && goal.trim() !== '');
      if (sanitized.investmentGoals.length === 0) {
        delete sanitized.investmentGoals; // Remove empty arrays
      }
    } else {
      delete sanitized.investmentGoals;
    }
    
    // Ensure tags is an array and filter out empty values
    if (sanitized.tags && Array.isArray(sanitized.tags)) {
      sanitized.tags = sanitized.tags.filter(tag => tag && tag.trim() !== '');
      if (sanitized.tags.length === 0) {
        delete sanitized.tags; // Remove empty arrays
      }
    } else {
      delete sanitized.tags;
    }
    
    // Clean up address object
    if (sanitized.address) {
      const addressFields = ['street', 'city', 'state', 'zipCode', 'country'];
      let hasAddressData = false;
      
      addressFields.forEach(field => {
        if (sanitized.address[field] === '' || sanitized.address[field] === undefined) {
          delete sanitized.address[field]; // Remove empty address fields
        } else if (sanitized.address[field]) {
          hasAddressData = true;
        }
      });
      
      // If no address data, remove address object entirely
      if (!hasAddressData) {
        delete sanitized.address;
      }
    }
    
    return sanitized;
  }

  /**
   * Create a new client profile
   * @returns {Promise<Object>} Created client data
   * @throws {ServiceError} If creation fails
   */
  async createClient(clientData) {
    try {
      const userId = await this.getCurrentUserId();

      // Sanitize the client data before sending to Amplify
      const sanitizedData = this.sanitizeClientData(clientData);

      const clientToCreate = {
        ...sanitizedData,
        userId,
        isActive: true
        // Note: Don't manually set createdAt/updatedAt - let Amplify handle these
      };

      this.logger.info('Creating client with sanitized data:', clientToCreate);

      const result = await this.client.models.Client.create(clientToCreate);

      if (result.errors) {
        throw new Error(`GraphQL errors: ${JSON.stringify(result.errors)}`);
      }

      return result;
    } catch (error) {
      this.logger.error('Error creating client:', error);

      // Re-throw if already a ServiceError
      if (error.name === 'ServiceError') {
        throw error;
      }

      // Wrap other errors with user-friendly message
      createNetworkError(`Failed to create client: ${error.message}`, error);
    }
  }

  /**
   * Get all clients for the current user
   */
  async getAllClients() {
    return this.getClients();
  }

  /**
   * Get all clients for the current user with optional filtering
   * @returns {Promise<Array>} Clients array
   * @throws {ServiceError} If query fails
   */
  async getClients(filters = {}) {
    try {
      const userId = await this.getCurrentUserId();

      let queryFilter = {
        userId: { eq: userId }
      };

      // Apply additional filters
      if (filters.isActive !== undefined) {
        queryFilter.isActive = { eq: filters.isActive };
      }

      if (filters.riskProfile) {
        queryFilter.riskProfile = { eq: filters.riskProfile };
      }

      if (filters.portfolioSize) {
        queryFilter.portfolioSize = { eq: filters.portfolioSize };
      }

      this.logger.info('Fetching clients with filter:', queryFilter);

      const result = await this.client.models.Client.list({
        filter: queryFilter
      });

      if (result.errors) {
        throw new Error(`GraphQL errors: ${JSON.stringify(result.errors)}`);
      }

      return result.data || [];
    } catch (error) {
      this.logger.error('Error fetching clients:', error);

      if (error.name === 'ServiceError') {
        throw error;
      }

      createNetworkError(`Failed to fetch clients: ${error.message}`, error);
    }
  }

  /**
   * Search clients by name or email (client-side search for performance)
   * @returns {Promise<Array>} Filtered clients
   * @throws {ServiceError} If search fails
   */
  async searchClients(searchTerm) {
    if (!searchTerm || searchTerm.trim().length === 0) {
      return await this.getClients();
    }

    const allClients = await this.getClients(); // Will throw if fails

    const searchTermLower = searchTerm.toLowerCase().trim();

    return allClients.filter(client => {
      const firstName = (client.firstName || '').toLowerCase();
      const lastName = (client.lastName || '').toLowerCase();
      const email = (client.email || '').toLowerCase();
      const fullName = `${firstName} ${lastName}`;

      return firstName.includes(searchTermLower) ||
             lastName.includes(searchTermLower) ||
             fullName.includes(searchTermLower) ||
             email.includes(searchTermLower);
    });
  }

  /**
   * Check if a client with the given email already exists (only active clients)
   */
  async checkDuplicateClient(email, excludeId = null) {
    try {
      if (!email) return false;
      
      // Only check active clients for duplicates - allow reusing emails from deleted clients
      const clients = await this.getClients({ isActive: true });
      const emailLower = email.toLowerCase().trim();
      
      return clients.some(client => {
        const clientEmail = (client.email || '').toLowerCase().trim();
        return clientEmail === emailLower && client.id !== excludeId;
      });
    } catch (error) {
      this.logger.error('Error checking duplicate client:', error);
      // Return false on error to not block user operations
      return false;
    }
  }

  /**
   * Get a specific client by ID
   * @returns {Promise<Object>} Client data
   * @throws {ServiceError} If fetch fails
   */
  async getClientById(id) {
    try {
      this.logger.info('Fetching client by ID:', id);

      const result = await this.client.models.Client.get({ id });

      if (result.errors) {
        throw new Error(`GraphQL errors: ${JSON.stringify(result.errors)}`);
      }

      return result.data;
    } catch (error) {
      this.logger.error('Error fetching client by ID:', error);

      if (error.name === 'ServiceError') {
        throw error;
      }

      createNetworkError(`Failed to fetch client: ${error.message}`, error);
    }
  }

  /**
   * Update an existing client
   * @returns {Promise<Object>} Updated client
   * @throws {ServiceError} If update fails
   */
  async updateClient(id, clientData) {
    try {
      // Sanitize the client data before sending to Amplify
      const sanitizedData = this.sanitizeClientData(clientData);

      const clientToUpdate = {
        id,
        ...sanitizedData
        // Note: Don't manually set updatedAt - let Amplify handle this
      };

      this.logger.info('Updating client with sanitized data:', clientToUpdate);

      const result = await this.client.models.Client.update(clientToUpdate);

      if (result.errors) {
        throw new Error(`GraphQL errors: ${JSON.stringify(result.errors)}`);
      }

      return result;
    } catch (error) {
      this.logger.error('Error updating client:', error);

      if (error.name === 'ServiceError') {
        throw error;
      }

      createNetworkError(`Failed to update client: ${error.message}`, error);
    }
  }

  /**
   * Delete a client (soft delete by setting isActive to false)
   * @returns {Promise<Object>} Delete result
   * @throws {ServiceError} If delete fails
   */
  async deleteClient(id, hardDelete = false) {
    try {
      this.logger.info('Deleting client:', id, 'Hard delete:', hardDelete);

      if (hardDelete) {
        // Permanent deletion
        const result = await this.client.models.Client.delete({ id });

        if (result.errors) {
          throw new Error(`GraphQL errors: ${JSON.stringify(result.errors)}`);
        }

        return result;
      } else {
        // Soft delete - just mark as inactive
        return await this.updateClient(id, {
          isActive: false,
          updatedAt: new Date().toISOString()
        });
      }
    } catch (error) {
      this.logger.error('Error deleting client:', error);

      if (error.name === 'ServiceError') {
        throw error;
      }

      createNetworkError(`Failed to delete client: ${error.message}`, error);
    }
  }

  /**
   * Get client statistics
   * @returns {Promise<Object>} Statistics object
   * @throws {ServiceError} If stats calculation fails
   */
  async getClientStats() {
    try {
      const clients = await this.getClients({ isActive: true }); // Will throw if fails

      const stats = {
        total: clients.length,
        byRiskProfile: {},
        byPortfolioSize: {},
        byNewsletterFrequency: {},
        recentlyAdded: 0
      };

      // Calculate one month ago
      const oneMonthAgo = new Date();
      oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);

      clients.forEach(client => {
        // Risk profile distribution
        const risk = client.riskProfile || 'Unknown';
        stats.byRiskProfile[risk] = (stats.byRiskProfile[risk] || 0) + 1;

        // Portfolio size distribution
        const portfolio = client.portfolioSize || 'Unknown';
        stats.byPortfolioSize[portfolio] = (stats.byPortfolioSize[portfolio] || 0) + 1;

        // Newsletter frequency distribution
        const frequency = client.newsletterFrequency || 'Monthly';
        stats.byNewsletterFrequency[frequency] = (stats.byNewsletterFrequency[frequency] || 0) + 1;

        // Recently added (last month)
        if (client.createdAt && new Date(client.createdAt) > oneMonthAgo) {
          stats.recentlyAdded++;
        }
      });

      return stats;
    } catch (error) {
      this.logger.error('Error getting client stats:', error);

      if (error.name === 'ServiceError') {
        throw error;
      }

      createNetworkError(`Failed to get client statistics: ${error.message}`, error);
    }
  }

  /**
   * Get clients for newsletter segmentation
   * @returns {Promise<Array>} Filtered clients
   * @throws {ServiceError} If query fails
   */
  async getClientsForNewsletter(criteria = {}) {
    const clients = await this.getClients({ isActive: true }); // Will throw if fails

    return clients.filter(client => {
      // Filter by risk profile if specified
      if (criteria.riskProfile && client.riskProfile !== criteria.riskProfile) {
        return false;
      }

      // Filter by portfolio size if specified
      if (criteria.portfolioSize && client.portfolioSize !== criteria.portfolioSize) {
        return false;
      }

      // Filter by newsletter frequency if specified
      if (criteria.newsletterFrequency && client.newsletterFrequency !== criteria.newsletterFrequency) {
        return false;
      }

      // Filter by investment goals if specified
      if (criteria.investmentGoals && criteria.investmentGoals.length > 0) {
        const clientGoals = client.investmentGoals || [];
        const hasMatchingGoal = criteria.investmentGoals.some(goal =>
          clientGoals.includes(goal)
        );
        if (!hasMatchingGoal) {
          return false;
        }
      }

      return true;
    });
  }

  /**
   * Update client's last contact date
   * @returns {Promise<Object>} Updated client
   * @throws {ServiceError} If update fails
   */
  async updateLastContact(id) {
    return await this.updateClient(id, {
      lastContactDate: new Date().toISOString()
    }); // Will throw if fails
  }

  /**
   * Add tags to a client
   * @returns {Promise<Object>} Updated client
   * @throws {ServiceError} If update fails or client not found
   */
  async addClientTags(id, newTags) {
    const client = await this.getClientById(id); // Will throw if fails

    if (!client) {
      createDataUnavailableError('Client not found');
    }

    const existingTags = client.tags || [];
    const uniqueTags = [...new Set([...existingTags, ...newTags])];

    return await this.updateClient(id, { tags: uniqueTags }); // Will throw if fails
  }

  /**
   * Remove tags from a client
   * @returns {Promise<Object>} Updated client
   * @throws {ServiceError} If update fails or client not found
   */
  async removeClientTags(id, tagsToRemove) {
    const client = await this.getClientById(id); // Will throw if fails

    if (!client) {
      createDataUnavailableError('Client not found');
    }

    const existingTags = client.tags || [];
    const filteredTags = existingTags.filter(tag => !tagsToRemove.includes(tag));

    return await this.updateClient(id, { tags: filteredTags }); // Will throw if fails
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Reminder Methods
  // TODO: Replace ReminderStore with GraphQL ClientReminder model when schema
  //       is deployed. All method signatures are designed for easy migration.
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Create a follow-up reminder for a client.
   * @param {object} params
   * @param {string} params.clientId - Required
   * @param {string} params.reminderDate - ISO string, must be in the future
   * @param {string} params.reminderType - follow_up | check_in | review_meeting | custom
   * @param {string} [params.campaignId]
   * @param {string} [params.notes]
   * @returns {Promise<object>} Created reminder
   */
  async createReminder({ clientId, campaignId, reminderDate, reminderType, notes }) {
    if (!clientId) {
      throw new Error('clientId is required to create a reminder');
    }
    if (!reminderDate) {
      throw new Error('reminderDate is required to create a reminder');
    }
    if (new Date(reminderDate) <= new Date()) {
      throw new Error('reminderDate must be in the future');
    }

    const type = reminderType || 'follow_up';
    const id = `reminder-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const reminder = {
      id,
      clientId,
      reminderDate,
      reminderType: type,
      status: 'PENDING',
      createdAt: new Date().toISOString(),
      ...(campaignId !== undefined && { campaignId }),
      ...(notes !== undefined && { notes }),
    };

    return this._reminderStore.add(reminder);
  }

  /**
   * Get reminders within a date range (inclusive).
   * @param {string} startDate - ISO string
   * @param {string} endDate - ISO string
   * @returns {Promise<Array>}
   */
  async getReminders(startDate, endDate) {
    const start = new Date(startDate);
    const end = new Date(endDate);
    return this._reminderStore.all().filter(r => {
      const d = new Date(r.reminderDate);
      return d >= start && d <= end;
    });
  }

  /**
   * Get all reminders for a specific client.
   * @param {string} clientId
   * @returns {Promise<Array>}
   */
  async getClientReminders(clientId) {
    return this._reminderStore.all().filter(r => r.clientId === clientId);
  }

  /**
   * Update a reminder's fields (status, notes, reminderDate, etc.).
   * @param {string} reminderId
   * @param {object} updates
   * @returns {Promise<object>} Updated reminder
   */
  async updateReminder(reminderId, updates) {
    const updated = this._reminderStore.update(reminderId, updates);
    if (!updated) {
      throw new Error(`Reminder ${reminderId} not found`);
    }
    return updated;
  }

  /**
   * Get upcoming reminders due in the next 7 days, PENDING only, sorted by date.
   * @returns {Promise<Array>}
   */
  async getUpcomingReminders() {
    const now = new Date();
    const sevenDaysOut = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
    return this._reminderStore
      .all()
      .filter(r => {
        const d = new Date(r.reminderDate);
        return d >= now && d <= sevenDaysOut && r.status === 'PENDING';
      })
      .sort((a, b) => new Date(a.reminderDate) - new Date(b.reminderDate));
  }

  /**
   * Delete a reminder permanently.
   * @param {string} reminderId
   * @returns {Promise<void>}
   */
  async deleteReminder(reminderId) {
    const removed = this._reminderStore.remove(reminderId);
    if (!removed) {
      throw new Error(`Reminder ${reminderId} not found`);
    }
  }

  /**
   * Create reminders for multiple clients at once.
   * @param {string[]} clientIds - Non-empty array of client IDs
   * @param {object} params - Shared reminder parameters
   * @param {string} params.reminderDate - ISO string, must be in the future
   * @param {string} params.reminderType - follow_up | check_in | review_meeting | custom
   * @param {string} [params.campaignId]
   * @param {string} [params.notes]
   * @param {boolean} [params.emailNotification]
   * @param {string} [params.selectedRule]
   * @returns {Promise<object[]>} Array of created reminders
   */
  async createBulkReminders(clientIds, params) {
    if (!clientIds || !Array.isArray(clientIds) || clientIds.length === 0) {
      throw new Error('clientIds must be a non-empty array');
    }
    const { reminderDate, reminderType, campaignId, notes, emailNotification, selectedRule } = params || {};
    if (!reminderDate) {
      throw new Error('reminderDate is required to create reminders');
    }
    if (new Date(reminderDate) <= new Date()) {
      throw new Error('reminderDate must be in the future');
    }

    const type = reminderType || 'follow_up';
    const results = [];
    for (const clientId of clientIds) {
      const id = `reminder-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const reminder = {
        id,
        clientId,
        reminderDate,
        reminderType: type,
        status: 'PENDING',
        createdAt: new Date().toISOString(),
        ...(campaignId !== undefined && { campaignId }),
        ...(notes !== undefined && { notes }),
        ...(emailNotification !== undefined && { emailNotification }),
        ...(selectedRule !== undefined && { selectedRule }),
      };
      results.push(this._reminderStore.add(reminder));
    }
    return results;
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Bulk import methods (merged from ClientImportService - Sprint 3 #51)
  // Preserves file-parsing, validation, and batch-create flow used by the
  // client import wizard. Logic copied verbatim from ClientImportService.js.
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Parse Excel file (.xlsx) into array of row objects
   * @param {File} file - Excel file to parse
   * @returns {Promise<{headers, data, totalRows}>}
   */
  async parseExcelFile(file) {
    return new Promise((resolve, reject) => {
      try {
        const reader = new FileReader();

        reader.onload = (e) => {
          try {
            const data = new Uint8Array(e.target.result);
            const workbook = XLSX.read(data, { type: 'array' });

            const firstSheetName = workbook.SheetNames[0];
            if (!firstSheetName) {
              reject(new Error('No sheets found in Excel file'));
              return;
            }

            const worksheet = workbook.Sheets[firstSheetName];
            const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

            if (jsonData.length === 0) {
              reject(new Error('Excel file is empty'));
              return;
            }

            const headers = jsonData[0];
            const rows = jsonData.slice(1);

            const parsedData = rows
              .filter(row => row && row.length > 0 && row.some(cell => cell !== null && cell !== undefined && cell !== ''))
              .map(row => {
                const obj = {};
                headers.forEach((header, index) => {
                  if (header) {
                    obj[header] = row[index] !== undefined && row[index] !== null ? String(row[index]).trim() : '';
                  }
                });
                return obj;
              });

            resolve({
              headers,
              data: parsedData,
              totalRows: parsedData.length
            });
          } catch (error) {
            reject(new Error(`Failed to parse Excel file: ${error.message}`));
          }
        };

        reader.onerror = () => {
          reject(new Error('Failed to read Excel file'));
        };

        reader.readAsArrayBuffer(file);
      } catch (error) {
        reject(new Error(`Failed to process Excel file: ${error.message}`));
      }
    });
  }

  /**
   * Parse CSV file into array of row objects
   */
  async parseCSVFile(file) {
    return new Promise((resolve, reject) => {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        transformHeader: (header) => header.trim(),
        complete: (results) => {
          try {
            if (results.errors && results.errors.length > 0) {
              this.logger.warn('CSV parsing warnings:', results.errors);
            }

            if (!results.data || results.data.length === 0) {
              reject(new Error('CSV file is empty or has no data rows'));
              return;
            }

            const headers = results.meta.fields || [];

            const data = results.data.filter(row => {
              return Object.values(row).some(value => value && value.trim() !== '');
            });

            resolve({
              headers,
              data,
              totalRows: data.length
            });
          } catch (error) {
            reject(new Error(`Failed to parse CSV file: ${error.message}`));
          }
        },
        error: (error) => {
          reject(new Error(`Failed to read CSV file: ${error.message}`));
        }
      });
    });
  }

  /**
   * Normalize risk profile value to match enum (case-insensitive)
   */
  normalizeRiskProfile(value) {
    if (!value) return null;

    const normalized = String(value).toLowerCase().trim();

    const exactMatch = this.VALID_RISK_PROFILES.find(
      profile => profile.toLowerCase() === normalized
    );
    if (exactMatch) return exactMatch;

    if (this.RISK_PROFILE_MAP[normalized]) {
      return this.RISK_PROFILE_MAP[normalized];
    }

    return null;
  }

  /**
   * Normalize portfolio size value to match enum (case-insensitive)
   */
  normalizePortfolioSize(value) {
    if (!value) return null;

    const normalized = String(value).toLowerCase().trim();

    const exactMatch = this.VALID_PORTFOLIO_SIZES.find(
      size => size.toLowerCase() === normalized
    );
    if (exactMatch) return exactMatch;

    if (this.PORTFOLIO_SIZE_MAP[normalized]) {
      return this.PORTFOLIO_SIZE_MAP[normalized];
    }

    return null;
  }

  /**
   * Validate email format
   */
  validateEmail(email) {
    if (!email) return false;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Validate a single row of client data
   * @param {Object} row - Row data object
   * @param {Object} mapping - Column mapping {columnName: clientField}
   * @returns {{isValid, errors, data}}
   */
  validateRow(row, mapping) {
    const errors = [];
    const data = {};

    Object.entries(mapping).forEach(([columnName, clientField]) => {
      if (clientField && clientField !== 'skip') {
        data[clientField] = row[columnName] || '';
      }
    });

    if (!data.firstName || data.firstName.trim() === '') {
      errors.push('First name is required');
    } else if (data.firstName.length > 50) {
      errors.push('First name must be 50 characters or less');
    }

    if (!data.lastName || data.lastName.trim() === '') {
      errors.push('Last name is required');
    } else if (data.lastName.length > 50) {
      errors.push('Last name must be 50 characters or less');
    }

    if (!data.email || data.email.trim() === '') {
      errors.push('Email is required');
    } else if (!this.validateEmail(data.email)) {
      errors.push('Invalid email format');
    }

    if (!data.riskProfile || data.riskProfile.trim() === '') {
      errors.push('Risk profile is required');
    } else {
      const normalized = this.normalizeRiskProfile(data.riskProfile);
      if (!normalized) {
        errors.push(`Invalid risk profile. Must be one of: ${this.VALID_RISK_PROFILES.join(', ')}`);
      } else {
        data.riskProfile = normalized;
      }
    }

    if (!data.portfolioSize || data.portfolioSize.trim() === '') {
      errors.push('Portfolio size is required');
    } else {
      data.portfolioSize = data.portfolioSize.trim();
    }

    if (data.phone) {
      data.phone = data.phone.trim();
    }

    if (data.notes) {
      data.notes = data.notes.trim();
    }

    if (data.tags && data.tags.trim()) {
      data.tags = data.tags
        .split(',')
        .map(tag => tag.trim())
        .filter(tag => tag.length > 0);
    } else {
      delete data.tags;
    }

    return {
      isValid: errors.length === 0,
      errors,
      data
    };
  }

  /**
   * Check if email is duplicate
   */
  async checkDuplicateEmail(email, userId) {
    try {
      return await this.checkDuplicateClient(email);
    } catch (error) {
      this.logger.error('Error checking duplicate email:', error);
      return false;
    }
  }

  /**
   * Import clients in batches
   * @param {Array} validatedData - Array of validated client data objects
   * @param {string} userId - Current user ID (retained for parity; not used here)
   * @returns {Promise<{imported, skipped, failed, errors}>}
   */
  async importClients(validatedData, userId) {
    const results = {
      imported: 0,
      skipped: 0,
      failed: 0,
      errors: []
    };

    const BATCH_SIZE = 25;
    const totalBatches = Math.ceil(validatedData.length / BATCH_SIZE);

    for (let i = 0; i < totalBatches; i++) {
      const start = i * BATCH_SIZE;
      const end = Math.min(start + BATCH_SIZE, validatedData.length);
      const batch = validatedData.slice(start, end);

      this.logger.info(`Processing batch ${i + 1}/${totalBatches} (${batch.length} clients)`);

      const batchPromises = batch.map(async (clientData) => {
        try {
          const isDuplicate = await this.checkDuplicateEmail(clientData.email, userId);
          if (isDuplicate) {
            results.skipped++;
            results.errors.push({
              email: clientData.email,
              name: `${clientData.firstName} ${clientData.lastName}`,
              error: 'Duplicate email - already exists'
            });
            return;
          }

          await this.createClient(clientData);
          results.imported++;
        } catch (error) {
          this.logger.error('Error importing client:', error);
          results.failed++;
          results.errors.push({
            email: clientData.email,
            name: `${clientData.firstName} ${clientData.lastName}`,
            error: error.message || 'Unknown error'
          });
        }
      });

      await Promise.all(batchPromises);
    }

    this.logger.info('Import complete:', results);
    return results;
  }

  /**
   * Validate client data before operations
   */
  validateClientData(clientData) {
    const errors = [];

    // Required fields validation
    if (!clientData.firstName || clientData.firstName.trim().length === 0) {
      errors.push('First name is required');
    }

    if (!clientData.lastName || clientData.lastName.trim().length === 0) {
      errors.push('Last name is required');
    }

    if (!clientData.email || clientData.email.trim().length === 0) {
      errors.push('Email is required');
    }

    if (!clientData.riskProfile) {
      errors.push('Risk profile is required');
    }

    if (!clientData.portfolioSize) {
      errors.push('Portfolio size is required');
    }

    // Email format validation (basic)
    if (clientData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clientData.email)) {
      errors.push('Invalid email format');
    }

    // Risk profile validation
    const validRiskProfiles = ['Conservative', 'Moderate', 'Aggressive', 'Very Aggressive'];
    if (clientData.riskProfile && !validRiskProfiles.includes(clientData.riskProfile)) {
      errors.push('Invalid risk profile');
    }

    // Portfolio size validation
    const validPortfolioSizes = ['$0-50K', '$50K-250K', '$250K-1M', '$1M-5M', '$5M+'];
    if (clientData.portfolioSize && !validPortfolioSizes.includes(clientData.portfolioSize)) {
      errors.push('Invalid portfolio size');
    }

    return errors;
  }
}

/**
 * #314 — Canonical lifecycle-stage values. Frozen so callers can't
 * silently expand the whitelist at runtime — keeping the open enum
 * to four buckets is a deliberate decision (broader segmentation
 * lives in the full #306 epic). Drift between this list and the
 * schema would be caught by the validation tests.
 */
export const LIFECYCLE_STAGES = Object.freeze([
  'accumulation',
  'preretirement',
  'retirement',
  'wealth-transfer',
]);

/**
 * #314 — Get all of the current user's clients filtered by lifecycle
 * stage. Pass `null` to get the "not set" bucket (clients without a
 * lifecycleStage value). Throws on unknown stage strings.
 */
ClientService.prototype.getClientsByLifecycleStage = async function (stage) {
  if (stage !== null && !LIFECYCLE_STAGES.includes(stage)) {
    throw new Error(
      `Invalid lifecycleStage: "${stage}". Expected one of: ${LIFECYCLE_STAGES.join(', ')} or null.`
    );
  }
  const all = await this.getClients();
  if (stage === null) {
    return all.filter((c) => c.lifecycleStage === null || c.lifecycleStage === undefined);
  }
  return all.filter((c) => c.lifecycleStage === stage);
};

/**
 * #314 — Update a single client's lifecycleStage. Pass `null` to
 * clear the value back to "not set". Throws on unknown stages or
 * missing client id.
 */
ClientService.prototype.updateLifecycleStage = async function (clientId, stage) {
  if (!clientId) {
    throw new Error('Client id is required to update lifecycleStage.');
  }
  if (stage !== null && !LIFECYCLE_STAGES.includes(stage)) {
    throw new Error(
      `Invalid lifecycleStage: "${stage}". Expected one of: ${LIFECYCLE_STAGES.join(', ')} or null.`
    );
  }
  const { data } = await this.client.models.Client.update({
    id: clientId,
    lifecycleStage: stage,
  });
  return data;
};

// Export singleton instance
const clientService = new ClientService();
export default clientService;
